import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  locationTypeSet,
  locationTypeSelector,
  geometryTypeSelector
} from 'redux/keyFieldsSlice';
import {
  numericFieldNamesSelector,
  getHumanReadableFormat,
  hasDerivedGeometrySelector,
  formatNameSelector
} from 'redux/schemaSlice';

import GeometryTypeSelect from './GeometryTypeSelect';
import SpatialReference from './SpatialReference';
import SingleGeometryField from './SingleGeometryField';
import XyFields from './XyFields';

import {
  Form,
  FormControl,
  Heading,
  StyledPanel
} from 'components/StyledComponents';
import Select from 'components/Select';

import { CalciteLabel, CalciteNotice, CalciteOption } from 'components/Calcite';

const LocationField: React.FC = () => {
  const dispatch = useDispatch();

  const numericFields = useSelector(numericFieldNamesSelector);
  const locationType = useSelector(locationTypeSelector);
  const geometryType = useSelector(geometryTypeSelector);
  const hasDerivedGeometry = useSelector(hasDerivedGeometrySelector);
  const formatName = useSelector(formatNameSelector);

  const renderLocationTypeControls = () => {
    switch (locationType) {
      case '__NONE__':
        return 'Data does not contain spatial attributes';
      case '__SINGLE__':
        return <SingleGeometryField />;
      case '__XY__':
        return <XyFields />;
      default:
        break;
    }
  };

  const renderContent = () => {
    // 1. Known location types
    if (hasDerivedGeometry) {
      return (
        <CalciteNotice kind="success" open>
          <div slot="message">
            {`Data format is ${getHumanReadableFormat(
              formatName
            )}. ArcGIS Velocity will handle the location for you.`}
          </div>
        </CalciteNotice>
      );
    }
    // 2. esri-json with missing location must be 'None' or include spatial reference
    if (formatName === 'esri-json') {
      return (
        <>
          <GeometryTypeSelect hasNone />
          {geometryType !== '__NONE__' && <SpatialReference />}
        </>
      );
    }
    // 3. geo-json with missing geometry needs user defined geometryType
    if (formatName === 'geo-json') {
      return <GeometryTypeSelect />;
    }

    // 4. Unknown location
    return (
      <>
        <FormControl>
          <CalciteLabel>Location type</CalciteLabel>
          <Select
            label="location"
            onChange={value =>
              dispatch(
                locationTypeSet({
                  locationType: value as LocationType,
                  numericFields
                })
              )
            }
            options={[
              <CalciteOption
                key="__SINGLE__"
                selected={locationType === '__SINGLE__'}
                value="__SINGLE__"
              >
                Single geometry field
              </CalciteOption>,
              <CalciteOption
                key="__XY__"
                selected={locationType === '__XY__'}
                value="__XY__"
              >
                X/Y fields
              </CalciteOption>,
              <CalciteOption
                key="__NONE__"
                selected={locationType === '__NONE__'}
                value="__NONE__"
              >
                None
              </CalciteOption>
            ]}
            value={locationType}
          />
        </FormControl>
        {renderLocationTypeControls()}
      </>
    );
  };

  return (
    <StyledPanel>
      <Heading>Location</Heading>
      <Form>{renderContent()}</Form>
    </StyledPanel>
  );
};

export default LocationField;
